/** Command. Currently this is just a function that takes the editor as an argument and returns the Editor for chainability.  */

import type { SvgIconComponent } from '@tunasong/icons'
import { capitalize, isPersistedEntity } from '@tunasong/models'
import type { Maturity } from '@tunasong/models'
import { isCoreElement } from '@tunasong/schemas'
import type { CoreElement, ElementType } from '@tunasong/schemas'
import type { DefaultGlobalContext } from '../app-context.js'
import type { Command, Template, TunaEditor, TunaPlugin } from '../plugin-types.js'
import type { StreamingCommand } from '../streaming-command.js'
import { Transforms } from '../slate-typescript.js'
import type { NodeEntry } from '../slate-typescript.js'
import { getElementMediaForPlugin } from './plugin-util.js'
import type { UserConfig } from '@tunasong/manifest'

/** Get a command by name */

interface GetGlobalCommands {
  plugins: TunaPlugin[]
  text?: string
  maturity?: Maturity
  globalContext: DefaultGlobalContext
}

interface GetEditorCommand<TCommandName extends string = string> {
  commandId: TCommandName
  plugins: TunaPlugin[]
  editor: TunaEditor
  /** Current text that the plugin can use to generate the command(s) */
  text?: string

  nodeEntry?: NodeEntry<CoreElement>

  maturity?: Maturity
  /** @todo move redux to global context */
  globalContext: DefaultGlobalContext

  /** User configuration */
  userConfig: UserConfig
}

/** Synthesize <Type>: Insert <Template> */
interface GetInsertCommands {
  editor: TunaEditor
  type?: ElementType
  nodeEntry?: NodeEntry<CoreElement>
  templates?: Template[]
}

const getInsertCommands = ({ editor, type, templates = [], nodeEntry }: GetInsertCommands): Command[] => {
  const commands: Command[] = []
  if (!type) {
    return commands
  }
  for (const template of templates) {
    // /** Insert only supported for CoreElements, i.e., templates with `children`. */

    const templateName = template.name ?? 'default'
    const node = template.template
    if (!isCoreElement(node)) {
      continue
    }
    // /** @todo this is probably broken now as only Entities have templates */
    const name = `${capitalize(type)}: Insert ${templateName !== 'default' ? capitalize(templateName) : '...'}`
    commands.push({
      id: `insert-${type}-${templateName}`,
      name,
      cmd: () => {
        const at = nodeEntry ? nodeEntry[1] : (editor.selection ?? undefined)
        Transforms.insertNodes(editor, [node, { text: '' }], { at, select: true })
        return editor
      },
    })
  }

  return commands
}

/**
 * Get the available global commands.
 *
 */
export const getGlobalCommands = ({
  plugins,
  maturity = 'stable',
  globalContext,
  text,
}: GetGlobalCommands): (Command | StreamingCommand)[] => {
  const commands: (Command | StreamingCommand)[] = []
  for (const plugin of plugins) {
    const { getGlobalCommands } = plugin

    const { icon: Icon, materialColor } = getElementMediaForPlugin(plugin)

    const pluginCommands = getGlobalCommands ? getGlobalCommands({ maturity, globalContext, text }) : []

    /** Add the commands with media from the plugin */
    for (const cmd of pluginCommands) {
      commands.push({ Icon: Icon as SvgIconComponent, color: materialColor, ...cmd } as Command | StreamingCommand)
    }
  }
  return commands
}

/**
 * Get the available editor commands for this context. If there are duplicates, we use the last defined ones.
 *
 */
export const getEditorCommands = ({
  editor,
  nodeEntry,
  plugins,
  text = '',
  maturity = 'stable',
  globalContext,
  userConfig,
}: Omit<GetEditorCommand, 'commandId'>): (Command | StreamingCommand)[] => {
  const commands: (Command | StreamingCommand)[] = []
  const entity = isPersistedEntity(editor.rootElement) ? editor.rootElement : undefined
  for (const plugin of plugins) {
    const { getEditorCommands, node, templates } = plugin

    const { icon: Icon, materialColor: color } = getElementMediaForPlugin(plugin)

    const pluginCommands = getEditorCommands
      ? getEditorCommands({ editor, nodeEntry, maturity, text, globalContext, entity, userConfig })
      : getInsertCommands({ type: node?.type, editor, templates })
    /** Add the commands with media from the plugin */
    for (const cmd of pluginCommands) {
      commands.push({ Icon: Icon as SvgIconComponent, color, ...cmd } as Command | StreamingCommand)
    }
  }
  return commands
}

//

export const getEditorCommand = ({
  commandId,
  editor,
  nodeEntry,
  plugins,
  text = '',
  maturity = 'stable',
  globalContext,
  userConfig,
}: GetEditorCommand): Command | StreamingCommand | null => {
  const commands = getEditorCommands({ editor, plugins, nodeEntry, text, maturity, globalContext, userConfig })
  return commands.find(command => command.id === commandId) ?? null
}

/** Run the editor command by name */
interface RunEditorCommand<T extends string = string> extends GetEditorCommand<T> {
  cmdParams?: Record<string, unknown>
}
export const runEditorCommand = async <TCommandName extends string = 'string'>({
  commandId,
  editor,
  plugins,
  nodeEntry,
  text = '',
  globalContext,
  maturity = 'stable',
  cmdParams = {},
  userConfig,
}: RunEditorCommand<TCommandName>) => {
  const command = getEditorCommand({ commandId, plugins, editor, nodeEntry, text, maturity, globalContext, userConfig })
  if (!command?.cmd) {
    throw new Error(`Command ${commandId} not found or command is null (not available)`)
  }
  return command.cmd(cmdParams)
}
